
import { useCallback, useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';
import { Button } from '@/components/ui/button';

type MelSource = 'admin_manual' | 'external_calculation';
type MelDirection = 'debit' | 'credit' | 'informative';
type MelMovementType =
  | 'debit_advance_approved'
  | 'credit_installment_collected'
  | 'credit_advance_cancelled'
  | 'credit_admin_adjustment'
  | 'debit_admin_adjustment'
  | 'cambio_mel_vigente';

interface MelSnapshot {
  companyId: string;
  liquidMel: number;
  outstanding: number;
  available: number;
  lastUpdatedAt: string | null;
  lastUpdatedByToken: string | null;
  lastSource: MelSource | null;
  note: string | null;
}

interface HistoryEntry {
  id: string;
  previousValue: string | null;
  newValue: string;
  delta: string;
  source: MelSource;
  changedByToken: string | null;
  externalReference: string | null;
  note: string | null;
  createdAt: string;
}

interface LedgerEntry {
  id: string;
  movementType: MelMovementType;
  direction: MelDirection;
  amount: string;
  advanceRequestId: string | null;
  note: string | null;
  createdByToken: string | null;
  createdAt: string;
}

interface Paginated<T> {
  data: T[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

const formatCLP = (n: number | string) => Number(n).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const MOVEMENT_LABELS: Record<MelMovementType, string> = {
  debit_advance_approved: 'Adelanto aprobado',
  credit_installment_collected: 'Cuota cobrada',
  credit_advance_cancelled: 'Adelanto cancelado',
  credit_admin_adjustment: 'Ajuste manual (crédito)',
  debit_admin_adjustment: 'Ajuste manual (débito)',
  cambio_mel_vigente: 'Cambio de línea',
};

export default function AdminMelPanel({ companyId }: { companyId: string }) {
  const [snapshot, setSnapshot] = useState<MelSnapshot | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [snap, hist, led] = await Promise.all([
        adminHttpClient.get<MelSnapshot>(getAdminEndpointUrl('mel', 'snapshot', { companyId })),
        adminHttpClient.get<Paginated<HistoryEntry>>(`${getAdminEndpointUrl('mel', 'history', { companyId })}?pageSize=10`),
        adminHttpClient.get<Paginated<LedgerEntry>>(`${getAdminEndpointUrl('mel', 'ledger', { companyId })}?pageSize=20`),
      ]);
      setSnapshot(snap);
      setHistory(hist.data);
      setLedger(led.data);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading && !snapshot) {
    return <div className="rounded-lg border p-6 text-sm text-muted-foreground">Cargando MEL…</div>;
  }

  if (!snapshot) return null;

  return (
    <div className="space-y-6">
      <section className="rounded-xl border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold">MEL líquido</h3>
            <p className="mt-1 text-sm text-muted-foreground">Línea de crédito vigente del comercio.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>Ajustar MEL</Button>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Líquido</p>
            <p className="mt-1 text-lg font-semibold">{formatCLP(snapshot.liquidMel)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ocupado</p>
            <p className="mt-1 text-lg font-semibold text-red-500">{formatCLP(snapshot.outstanding)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Disponible</p>
            <p className={`mt-1 text-lg font-semibold ${snapshot.available < 0 ? 'text-red-500' : 'text-accent-green'}`}>{formatCLP(snapshot.available)}</p>
          </div>
        </div>

        {snapshot.lastUpdatedAt && (
          <p className="mt-4 text-xs text-muted-foreground">
            Último cambio: {formatDate(snapshot.lastUpdatedAt)} — origen {snapshot.lastSource ?? '—'}
            {snapshot.lastUpdatedByToken ? ` · token ${snapshot.lastUpdatedByToken}` : ''}
            {snapshot.note ? ` · "${snapshot.note}"` : ''}
          </p>
        )}
      </section>

      <section className="rounded-xl border bg-card p-6">
        <h3 className="text-base font-semibold">Histórico MEL líquido</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3 text-right">Anterior</th>
                <th className="py-2 pr-3 text-right">Nuevo</th>
                <th className="py-2 pr-3 text-right">Δ</th>
                <th className="py-2 pr-3">Origen</th>
                <th className="py-2 pr-3">Token</th>
                <th className="py-2 pr-3">Nota</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">Sin cambios registrados.</td>
                </tr>
              )}
              {history.map((h) => (
                <tr key={h.id} className="border-t border-border">
                  <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(h.createdAt)}</td>
                  <td className="py-2 pr-3 text-right">{h.previousValue != null ? formatCLP(h.previousValue) : '—'}</td>
                  <td className="py-2 pr-3 text-right font-medium">{formatCLP(h.newValue)}</td>
                  <td className={`py-2 pr-3 text-right ${Number(h.delta) < 0 ? 'text-red-500' : 'text-accent-green'}`}>{Number(h.delta) >= 0 ? '+' : ''}{formatCLP(h.delta)}</td>
                  <td className="py-2 pr-3 capitalize">{h.source.replace('_', ' ')}</td>
                  <td className="py-2 pr-3 text-xs font-mono">{h.changedByToken ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground truncate max-w-xs">{h.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold">Ledger MEL</h3>
            <p className="mt-1 text-sm text-muted-foreground">Débitos por aprobación, créditos por cobros y ajustes manuales.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setAdjustOpen(true)}>Ajuste manual</Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Dirección</th>
                <th className="py-2 pr-3 text-right">Monto</th>
                <th className="py-2 pr-3">Referencia</th>
                <th className="py-2 pr-3">Token</th>
                <th className="py-2 pr-3">Nota</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-4 text-center text-muted-foreground">Sin movimientos.</td>
                </tr>
              )}
              {ledger.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(l.createdAt)}</td>
                  <td className="py-2 pr-3">{MOVEMENT_LABELS[l.movementType] ?? l.movementType}</td>
                  <td className="py-2 pr-3">
                    {l.direction === 'credit' && <span className="text-accent-green">Crédito</span>}
                    {l.direction === 'debit' && <span className="text-red-500">Débito</span>}
                    {l.direction === 'informative' && <span className="text-slate-400">Info</span>}
                  </td>
                  <td className="py-2 pr-3 text-right">{formatCLP(l.amount)}</td>
                  <td className="py-2 pr-3 text-xs font-mono">{l.advanceRequestId ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs font-mono">{l.createdByToken ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground truncate max-w-xs">{l.note ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {editOpen && <UpdateMelModal companyId={companyId} initial={snapshot.liquidMel} onClose={() => setEditOpen(false)} onSuccess={async () => { setEditOpen(false); await refresh(); }} />}
      {adjustOpen && <AdjustmentModal companyId={companyId} onClose={() => setAdjustOpen(false)} onSuccess={async () => { setAdjustOpen(false); await refresh(); }} />}
    </div>
  );
}

function UpdateMelModal({ companyId, initial, onClose, onSuccess }: { companyId: string; initial: number; onClose: () => void; onSuccess: () => Promise<void> }) {
  const [value, setValue] = useState<string>(String(initial));
  const [source, setSource] = useState<MelSource>('admin_manual');
  const [externalReference, setExternalReference] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminHttpClient.put(getAdminEndpointUrl('mel', 'update', { companyId }), {
        liquidMel: Number(value),
        source,
        externalReference: externalReference || undefined,
        note: note || undefined,
      });
      await onSuccess();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
        <h3 className="text-lg font-semibold">Ajustar MEL líquido</h3>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <label className="block text-sm">
            Nuevo valor (CLP)
            <input type="number" min="0" step="1" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" required />
          </label>
          <label className="block text-sm">
            Origen
            <select value={source} onChange={(e) => setSource(e.target.value as MelSource)} className="mt-1 w-full rounded-md border px-3 py-2">
              <option value="admin_manual">Manual (admin)</option>
              <option value="external_calculation">Cálculo externo</option>
            </select>
          </label>
          {source === 'external_calculation' && (
            <label className="block text-sm">
              Referencia externa
              <input value={externalReference} onChange={(e) => setExternalReference(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" placeholder="ej. execution_id" />
            </label>
          )}
          <label className="block text-sm">
            Nota
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" rows={3} />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AdjustmentModal({ companyId, onClose, onSuccess }: { companyId: string; onClose: () => void; onSuccess: () => Promise<void> }) {
  const [direction, setDirection] = useState<'debit' | 'credit'>('credit');
  const [amount, setAmount] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim()) {
      alert('La nota es obligatoria');
      return;
    }
    setSaving(true);
    try {
      await adminHttpClient.post(getAdminEndpointUrl('mel', 'adjustment', { companyId }), {
        direction,
        amount: Number(amount),
        note,
      });
      await onSuccess();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
        <h3 className="text-lg font-semibold">Ajuste manual del ledger MEL</h3>
        <p className="mt-1 text-xs text-muted-foreground">El asiento queda append-only; correcciones posteriores son contra-asientos.</p>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <label className="block text-sm">
            Dirección
            <select value={direction} onChange={(e) => setDirection(e.target.value as 'debit' | 'credit')} className="mt-1 w-full rounded-md border px-3 py-2">
              <option value="credit">Crédito (libera MEL)</option>
              <option value="debit">Débito (ocupa MEL)</option>
            </select>
          </label>
          <label className="block text-sm">
            Monto (CLP)
            <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" required />
          </label>
          <label className="block text-sm">
            Nota (obligatoria)
            <textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2" rows={3} required />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Guardando…' : 'Registrar ajuste'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
